<script setup lang="ts">
import { computed, onMounted, useTemplateRef } from "vue";
import { useRoute, useRouter } from "vue-router";

import TabPanel from "@/components/ui/TabPanel.vue";
import TabPanelContent from "@/components/ui/TabPanelContent.vue";
import DefaultPageLayout from "@/layouts/DefaultPageLayout.vue";
import { ProjectDetailSections, RouteNames } from "@/router";
import { useRemainingHeight } from "@/services/remainingHeight";
import ProjectDetailViewComparisonList from "@/views/projects/ProjectDetailViewComparisonList.vue";
import ProjectDetailViewExperimentsSearch from "@/views/projects/ProjectDetailViewExperimentsSearch.vue";
import ProjectDetailViewPresentations from "@/views/projects/ProjectDetailViewPresentations.vue";

const route = useRoute();
const router = useRouter();
const tenantName = computed(() => route.params.tenantName as string);
const projectName = computed(() => route.params.projectName as string);
const tabs = useTemplateRef<InstanceType<typeof TabPanel>>("tabs");
const { remainingHeight, computeRemainingHeight } = useRemainingHeight(".tabs");

function onTabChange(tabName: string) {
  const { href } = router.resolve({
    name: RouteNames.ProjectDetail,
    params: {
      tenantName: tenantName.value,
      projectName: projectName.value,
      section: tabName,
    },
  });
  history.pushState({}, "", href);
}

onMounted(() => {
  if (tabs.value) {
    const routeSection = route.params.section as string | undefined;
    const section =
      routeSection && routeSection.length > 0 ? routeSection : ProjectDetailSections.Reports;
    tabs.value.selectTab(section);
  }
});
</script>

<template>
  <DefaultPageLayout
    :breadcrumb-items="[
      {
        label: tenantName,
        to: { name: RouteNames.Projects, params: { tenantName } },
        icon: 'icon-masonry',
      },
      { label: projectName, icon: 'icon-punch-card', iconColor: 'var(--color-background-purple)' },
    ]"
  >
    <div class="experiment-view">
      <TabPanel
        ref="tabs"
        :style="{ '--content-height': `${remainingHeight}px` }"
        :uses-anchor="false"
        @tab-change="onTabChange"
        @vue:mounted="computeRemainingHeight"
      >
        <TabPanelContent :name="ProjectDetailSections.Reports" icon="icon-cube">
          <ProjectDetailViewExperimentsSearch />
        </TabPanelContent>
        <TabPanelContent :name="ProjectDetailSections.Comparisons" icon="icon-scale">
          <ProjectDetailViewComparisonList />
        </TabPanelContent>
        <TabPanelContent :name="ProjectDetailSections.Presentations" icon="icon-book">
          <ProjectDetailViewPresentations />
        </TabPanelContent>
      </TabPanel>
    </div>
  </DefaultPageLayout>
</template>

<style scoped>
.experiment-view {
  :deep(.tab-selector) {
    margin-bottom: var(--spacing-24);
  }

  :deep(.tab-content) {
    height: calc(var(--content-height) - var(--spacing-20));
  }
}
</style>
